/**
 * ManagerialDashboard — Couche de pilotage décisionnel pour SADA B2B.
 *
 * Affiche :
 *  - Indicateurs financiers (encaissé, attendu, à relancer, échu, ...)
 *  - Pipeline avec nombre + montant par étape
 *  - Performance (délais moyens, taux conversion, retards, blocages)
 *  - Comparaison mois courant vs mois précédent
 *
 * Cette couche s'ajoute AU-DESSUS du dashboard opérationnel existant
 * (les 7 KPI tuiles d'origine restent intactes).
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  TrendingUp, TrendingDown, ChevronDown, ChevronUp, Wallet, AlertTriangle,
  CheckCircle2, Hourglass, BarChart3, Filter, RefreshCw, Building2,
} from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('dermascan_token')}` });

const fmtFCFA = (n) => {
  if (n == null || isNaN(n)) return '—';
  return Number(n).toLocaleString('fr-FR') + ' FCFA';
};

const fmtHours = (h) => {
  if (h == null) return '—';
  if (h < 24) return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} j`;
};

const fmtPct = (p) => (p == null ? '—' : `${p}%`);

const STATUS_LABELS_SHORT = {
  demande_recue: 'Demande reçue',
  en_cours_cotation: 'En cotation',
  cotation_envoyee: 'Cotation envoyée',
  validee: 'Validée',
  en_preparation: 'En préparation',
  prete: 'Prête',
  livree: 'Livrée',
  retiree: 'Retirée',
  cloturee: 'Clôturée',
  annulee: 'Annulée',
  refusee: 'Refusée',
};

// ── Delta entre 2 valeurs (current vs previous) ─────────────────────────────
const Delta = ({ current, previous, isPct = false, inverse = false }) => {
  if (current == null || previous == null) {
    return <span className="text-[10px] text-zinc-400">vs N-1 : —</span>;
  }
  if (previous === 0 && current === 0) {
    return <span className="text-[10px] text-zinc-400">vs N-1 : —</span>;
  }
  const diff = current - previous;
  const pct = previous === 0 ? null : (100 * diff) / Math.abs(previous);
  // inverse=true : une baisse est une bonne nouvelle (ex: retards, échu)
  const isPositive = inverse ? diff < 0 : diff > 0;
  const color = diff === 0 ? 'text-zinc-400' : isPositive ? 'text-emerald-600' : 'text-rose-600';
  const Icon = diff === 0 ? null : (isPositive ? TrendingUp : TrendingDown);
  return (
    <span className={`text-[10px] inline-flex items-center gap-0.5 ${color}`}>
      {Icon && <Icon className="w-3 h-3" />}
      {isPct
        ? `${diff > 0 ? '+' : ''}${diff.toFixed(1)} pts`
        : pct != null
          ? `${diff > 0 ? '+' : ''}${pct.toFixed(0)}%`
          : `${diff > 0 ? '+' : ''}${Number(diff).toLocaleString('fr-FR')}`}
    </span>
  );
};

const KpiCard = ({ icon: Icon, label, value, sub, delta, color = 'text-zinc-700', testId }) => (
  <div className="p-3 bg-white rounded-lg border border-zinc-200" data-testid={testId}>
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0">
        <p className="text-[10px] text-zinc-500 uppercase tracking-wide leading-tight">{label}</p>
        <p className={`text-lg font-bold mt-1 tabular-nums truncate ${color}`} title={typeof value === 'string' ? value : ''}>{value}</p>
        {sub && <p className="text-[10px] text-zinc-400 leading-tight mt-0.5">{sub}</p>}
      </div>
      {Icon && <Icon className={`w-4 h-4 shrink-0 ${color}`} />}
    </div>
    {delta && <div className="mt-1">{delta}</div>}
  </div>
);

const PERIOD_OPTIONS = [
  { id: 'current_month', label: 'Mois en cours' },
  { id: 'previous_month', label: 'Mois précédent' },
  { id: 'last_30_days', label: '30 derniers jours' },
];

const ManagerialDashboard = ({ clinics, users, refreshKey }) => {
  const [open, setOpen] = useState(true);
  const [period, setPeriod] = useState('current_month');
  const [clinicId, setClinicId] = useState('');
  const [userId, setUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (clinicId) params.append('clinic_id', clinicId);
      if (userId) params.append('user_id', userId);
      const r = await axios.get(`${API}/b2b/analytics?${params}`, { headers: auth() });
      setData(r.data);
    } catch (e) {
      setData(null);
    } finally { setLoading(false); }
  };

  // Recharger à chaque changement de filtres ou refreshKey externe.
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [period, clinicId, userId, refreshKey, open]);

  const cur = data?.current;
  const prev = data?.previous;
  const fin = cur?.financial || {};
  const finPrev = prev?.financial || {};
  const perf = cur?.performance || {};
  const perfPrev = prev?.performance || {};

  const pipelineRows = useMemo(() => {
    if (!cur?.pipeline) return [];
    const order = ['demande_recue', 'en_cours_cotation', 'cotation_envoyee', 'validee', 'en_preparation', 'prete', 'livree', 'retiree', 'cloturee', 'annulee', 'refusee'];
    return order
      .map((s) => ({
        id: s, label: STATUS_LABELS_SHORT[s] || s,
        cur: cur.pipeline[s] || { count: 0, montant: 0 },
        prev: prev?.pipeline?.[s] || { count: 0, montant: 0 },
      }))
      .filter((r) => r.cur.count > 0 || r.prev.count > 0);
  }, [cur, prev]);

  return (
    <Card className="border-zinc-200 mb-4" data-testid="b2b-managerial-dashboard">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-violet-600" />
            <h3 className="text-sm font-bold text-zinc-800">Pilotage décisionnel</h3>
            <span className="text-[11px] text-zinc-500">· Montants · Délais · Conversions</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setOpen((o) => !o)} data-testid="b2b-pilotage-toggle">
            {open ? <><ChevronUp className="w-4 h-4 mr-1" /> Replier</> : <><ChevronDown className="w-4 h-4 mr-1" /> Déplier</>}
          </Button>
        </div>

        {open && (
          <div className="mt-4 space-y-4">
            {/* Filtres */}
            <div className="flex items-end gap-2 flex-wrap p-2 bg-zinc-50 rounded-lg">
              <div className="flex items-center gap-1 text-xs text-zinc-500"><Filter className="w-3.5 h-3.5" /> Filtres</div>
              <select className="h-8 px-2 rounded-md border border-zinc-300 text-xs bg-white"
                value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="b2b-pilotage-period">
                {PERIOD_OPTIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
              <select className="h-8 px-2 rounded-md border border-zinc-300 text-xs bg-white"
                value={clinicId} onChange={(e) => setClinicId(e.target.value)} data-testid="b2b-pilotage-clinic">
                <option value="">Toutes cliniques</option>
                {(clinics || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select className="h-8 px-2 rounded-md border border-zinc-300 text-xs bg-white"
                value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="b2b-pilotage-user">
                <option value="">Tous utilisateurs</option>
                {(users || []).map((u) => <option key={u.id} value={u.id}>{u.name || u.phone}</option>)}
              </select>
              <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="b2b-pilotage-refresh">
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Rafraîchir
              </Button>
              {data && (
                <p className="text-[10px] text-zinc-500 ml-auto">
                  Période : <strong>{data.period.label}</strong> &nbsp;·&nbsp; Comparée à : <strong>{data.previous_period.label}</strong>
                </p>
              )}
            </div>

            {!data ? (
              <p className="text-xs text-zinc-400 italic p-3">Chargement…</p>
            ) : (
              <>
                {/* Section Financier */}
                <div>
                  <p className="text-[11px] font-semibold text-zinc-600 uppercase mb-2">💰 Financier (FCFA)</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="b2b-financial-grid">
                    <KpiCard testId="b2b-fin-encaisse" icon={Wallet} color="text-emerald-700" label="Encaissé (période)"
                      value={fmtFCFA(fin.encaisse)} delta={<Delta current={fin.encaisse} previous={finPrev.encaisse} />} />
                    <KpiCard testId="b2b-fin-attendu" icon={Hourglass} color="text-indigo-700" label="Attendu (période)"
                      value={fmtFCFA(fin.attendu_periode)} delta={<Delta current={fin.attendu_periode} previous={finPrev.attendu_periode} />} />
                    <KpiCard testId="b2b-fin-relancer" icon={AlertTriangle} color="text-amber-700" label="À relancer"
                      value={fmtFCFA(fin.a_relancer)} delta={<Delta current={fin.a_relancer} previous={finPrev.a_relancer} inverse />} />
                    <KpiCard testId="b2b-fin-echu" icon={AlertTriangle} color="text-rose-700" label="Échu non payé"
                      value={fmtFCFA(fin.echu_non_paye)} delta={<Delta current={fin.echu_non_paye} previous={finPrev.echu_non_paye} inverse />} />
                    <KpiCard testId="b2b-fin-cot-attente" icon={Hourglass} color="text-amber-700" label="Cotations non validées"
                      value={fmtFCFA(fin.cotations_envoyees_non_validees)} delta={<Delta current={fin.cotations_envoyees_non_validees} previous={finPrev.cotations_envoyees_non_validees} />} />
                    <KpiCard testId="b2b-fin-val-noncloturee" icon={CheckCircle2} color="text-cyan-700" label="Validé non clôturé"
                      value={fmtFCFA(fin.validee_non_cloturee)} delta={<Delta current={fin.validee_non_cloturee} previous={finPrev.validee_non_cloturee} />} />
                  </div>
                </div>

                {/* Section Pipeline */}
                <div>
                  <p className="text-[11px] font-semibold text-zinc-600 uppercase mb-2">🔄 Pipeline (dossiers créés dans la période)</p>
                  {pipelineRows.length === 0 ? (
                    <p className="text-xs text-zinc-400 italic">Aucun dossier dans la période sélectionnée.</p>
                  ) : (
                    <div className="overflow-x-auto" data-testid="b2b-pipeline-table">
                      <table className="w-full text-xs">
                        <thead className="text-zinc-500 border-b border-zinc-200">
                          <tr>
                            <th className="text-left py-1.5 px-2">Étape</th>
                            <th className="text-right py-1.5 px-2">Nb</th>
                            <th className="text-right py-1.5 px-2">Montant (FCFA)</th>
                            <th className="text-right py-1.5 px-2 text-zinc-400">Nb N-1</th>
                            <th className="text-right py-1.5 px-2 text-zinc-400">Montant N-1</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pipelineRows.map((r) => (
                            <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                              <td className="py-1.5 px-2 font-medium">{r.label}</td>
                              <td className="py-1.5 px-2 text-right tabular-nums font-semibold">{r.cur.count}</td>
                              <td className="py-1.5 px-2 text-right tabular-nums">{Number(r.cur.montant).toLocaleString('fr-FR')}</td>
                              <td className="py-1.5 px-2 text-right tabular-nums text-zinc-400">{r.prev.count}</td>
                              <td className="py-1.5 px-2 text-right tabular-nums text-zinc-400">{Number(r.prev.montant).toLocaleString('fr-FR')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Section Performance */}
                <div>
                  <p className="text-[11px] font-semibold text-zinc-600 uppercase mb-2">⚡ Performance</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="b2b-perf-grid">
                    <KpiCard testId="b2b-perf-delai-cot" icon={Hourglass} color="text-indigo-700" label="Délai cotation moyen"
                      value={fmtHours(perf.delai_moyen_cotation_h)} sub="demande → cotation"
                      delta={<Delta current={perf.delai_moyen_cotation_h} previous={perfPrev.delai_moyen_cotation_h} inverse />} />
                    <KpiCard testId="b2b-perf-delai-exec" icon={Hourglass} color="text-indigo-700" label="Délai exécution moyen"
                      value={fmtHours(perf.delai_moyen_execution_h)} sub="validation → livraison"
                      delta={<Delta current={perf.delai_moyen_execution_h} previous={perfPrev.delai_moyen_execution_h} inverse />} />
                    <KpiCard testId="b2b-perf-conv-global" icon={CheckCircle2} color="text-emerald-700" label="Conversion globale"
                      value={fmtPct(perf.taux_conversion_global_pct)} sub="demande → validée"
                      delta={<Delta current={perf.taux_conversion_global_pct} previous={perfPrev.taux_conversion_global_pct} isPct />} />
                    <KpiCard testId="b2b-perf-conv-cot" icon={CheckCircle2} color="text-emerald-700" label="Acceptation cotation"
                      value={fmtPct(perf.taux_acceptation_cotation_pct)} sub="cotation → validée"
                      delta={<Delta current={perf.taux_acceptation_cotation_pct} previous={perfPrev.taux_acceptation_cotation_pct} isPct />} />
                    <KpiCard testId="b2b-perf-livraison-temps" icon={CheckCircle2} color="text-emerald-700" label="Livraison à temps"
                      value={fmtPct(perf.taux_livraison_a_temps_pct)} sub="sous délai prévu"
                      delta={<Delta current={perf.taux_livraison_a_temps_pct} previous={perfPrev.taux_livraison_a_temps_pct} isPct />} />
                    <KpiCard testId="b2b-perf-cot-24h" icon={CheckCircle2} color="text-emerald-700" label="Cotations sous 24h"
                      value={fmtPct(perf.taux_cotation_sous_24h_pct)} sub="demande → cotation < 24h"
                      delta={<Delta current={perf.taux_cotation_sous_24h_pct} previous={perfPrev.taux_cotation_sous_24h_pct} isPct />} />
                    <KpiCard testId="b2b-perf-retard" icon={AlertTriangle} color="text-rose-700" label="Dossiers en retard"
                      value={perf.nb_en_retard ?? 0} sub=">48h sans action"
                      delta={<Delta current={perf.nb_en_retard} previous={perfPrev.nb_en_retard} inverse />} />
                    <KpiCard testId="b2b-perf-bloque" icon={AlertTriangle} color="text-rose-700" label="Dossiers bloqués"
                      value={perf.nb_bloques ?? 0} sub="observation blocage"
                      delta={<Delta current={perf.nb_bloques} previous={perfPrev.nb_bloques} inverse />} />
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ManagerialDashboard;
